import { backend, CredentialsResponse } from "Backend";
import { useSnackbar } from "notistack";
import { useEffect, useState } from "react";

import { Button, Card, Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";
import { Add } from "@mui/icons-material";
import LoadingPattern from "components/LoadingPattern";
import CopyToClipboard from "components/CopyToClipboard";
import { useParamNumbers } from "helpers/hooks/useParamNumbers";
import { Navigate } from "react-router";
import { selectUserById } from "redux/slices/client";
import { useAppSelector } from "redux/hooks";

interface CredCopyProps {
  text: string;
}

const CredCopy = (props: CredCopyProps) => {
  return (
    <>
      <span
        style={{
          fontFamily: "monospace",
        }}>
        {props.text}
      </span>
      <CopyToClipboard text={props.text} />
    </>
  );
};

const UserApiAccess = () => {
  const { userId } = useParamNumbers(["userId"]);
  const user = useAppSelector((state) => selectUserById(state, userId));

  if (userId === undefined || user === undefined) {
    return <Navigate to=".." />;
  }

  const [userApiCredentials, setUserApiCredentials] = useState<CredentialsResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    loadUserApiCredentials(userId);
  }, [userId]);

  function loadUserApiCredentials(userId: number) {
    setLoading(true);
    backend
      .getUserApiCredentials(userId)
      .then((response) => {
        setLoading(false);
        const credentials = response.credentials;
        setUserApiCredentials(credentials);
      })
      .catch(() => {
        setLoading(false);
        enqueueSnackbar("Unable to load API key pairs", {
          variant: "error",
        });
        setUserApiCredentials([]);
      });
  }

  function createUserApiCredentials(userId: number) {
    backend
      .createUserApiCredential(userId)
      .then((response) => {
        enqueueSnackbar("API key pair created", { variant: "success" });
        const credential = response.credential;
        setUserApiCredentials((prev) => {
          return [...prev, credential];
        });
      })
      .catch(() => {
        enqueueSnackbar("Unable to create an API key pair", {
          variant: "error",
        });
        setUserApiCredentials([]);
      });
  }

  return (
    <div className="flex flex-col">
      <div className="flex justify-between items-center mb-4">
        <p className="text-2xl font-bold mb-2">API Access</p>
        <Button
          color="primary"
          variant="contained"
          disabled={user.deactivated || loading}
          onClick={() => {
            createUserApiCredentials(userId);
          }}
          startIcon={<Add />}>
          Create
        </Button>
      </div>
      {loading && (
        <div className="flex justify-center items-center h-full">
          <LoadingPattern />
        </div>
      )}
      {
        // Card for when there are no existing credentials
        userApiCredentials.length === 0 && !loading && (
          <Card>
            <div className="p-4">
              <p className="text-sm">
                This user does not have any API key pairs. Click the button above to create one.
              </p>
            </div>
          </Card>
        )
      }
      {/* Card for when there are existing credentials */}
      {userApiCredentials.length > 0 && !loading && (
        <Card>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Client ID</TableCell>
                <TableCell>Credential Secret</TableCell>
                <TableCell>Last Used</TableCell>
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {userApiCredentials.map((cred) => {
                return (
                  <TableRow key={cred.id}>
                    <TableCell>
                      <CredCopy text={cred.credentialId} />
                    </TableCell>

                    <TableCell>
                      <CredCopy text={cred.credentialSecret} />
                    </TableCell>
                    <TableCell>{cred.lastUsed !== null ? new Date(cred.lastUsed).toLocaleString() : "Never"}</TableCell>
                    <TableCell>
                      <Button
                        onClick={() => {
                          backend.revokeUserApiCredential(cred.id).then(() => {
                            enqueueSnackbar("Revoked API key pair", { variant: "success" });
                            loadUserApiCredentials(userId);
                          });
                        }}
                        color="error">
                        Revoke
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
};

export default UserApiAccess;
